import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";

const navItems = [
  { label: "首页", to: "/" },
  { label: "资讯", to: "/news" },
  { label: "作品", to: "/works" },
  { label: "关于", to: "/about" },
  { label: "联系", to: "/contact" },
];

export default function SiteHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [scope, setScope] = useState<"news" | "works">("news");
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setSearchOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  const submitSearch = (event?: React.FormEvent) => {
    if (event) event.preventDefault();
    const q = keyword.trim();
    if (!q) return;
    navigate(`/${scope}?q=${encodeURIComponent(q)}`);
    setSearchOpen(false);
    setKeyword("");
  };

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-colors duration-300 ${
        scrolled
          ? "bg-background-50/95 backdrop-blur border-b border-background-200"
          : "bg-background-50 border-b border-background-100"
      }`}
    >
      <div className="mx-auto flex h-16 md:h-20 items-center justify-between gap-4 px-4 md:px-6 lg:px-10">
        <Link to="/" className="flex items-center gap-2 shrink-0">
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 font-heading text-lg font-bold text-background-50">
            津
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-heading text-lg font-bold tracking-wide text-foreground-950">
              张津瑜
            </span>
            <span className="font-label text-[10px] uppercase tracking-[0.2em] text-foreground-500">
              Brand
            </span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              className={({ isActive }) =>
                `relative rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                  isActive
                    ? "text-primary-600"
                    : "text-foreground-700 hover:text-primary-600"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {item.label}
                  <span
                    className={`absolute inset-x-4 -bottom-0.5 h-0.5 rounded-full bg-accent-500 transition-opacity ${
                      isActive ? "opacity-100" : "opacity-0"
                    }`}
                  />
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setSearchOpen((v) => !v)}
            aria-label="搜索"
            className="flex h-10 w-10 items-center justify-center rounded-md text-foreground-700 transition-colors hover:bg-background-100 hover:text-primary-600"
          >
            <i className="ri-search-line text-xl" />
          </button>
          <Link
            to="/contact"
            className="hidden md:inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-mail-send-line text-base" />
            合作咨询
          </Link>
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label="菜单"
            className="flex h-10 w-10 items-center justify-center rounded-md text-foreground-800 transition-colors hover:bg-background-100 md:hidden"
          >
            <i className={menuOpen ? "ri-close-line text-2xl" : "ri-menu-line text-2xl"} />
          </button>
        </div>
      </div>

      {searchOpen && (
        <div className="border-t border-background-200 bg-background-50">
          <form
            onSubmit={submitSearch}
            className="mx-auto flex max-w-3xl flex-col gap-3 px-4 py-4 sm:flex-row sm:items-center md:px-6"
          >
            <div className="flex rounded-md border border-background-300 bg-background-100 p-1">
              <button
                type="button"
                onClick={() => setScope("news")}
                className={`rounded-md px-3 py-1.5 text-sm transition-colors ${
                  scope === "news"
                    ? "bg-primary-500 text-background-50"
                    : "text-foreground-700 hover:text-primary-600"
                }`}
              >
                资讯
              </button>
              <button
                type="button"
                onClick={() => setScope("works")}
                className={`rounded-md px-3 py-1.5 text-sm transition-colors ${
                  scope === "works"
                    ? "bg-primary-500 text-background-50"
                    : "text-foreground-700 hover:text-primary-600"
                }`}
              >
                作品
              </button>
            </div>
            <div className="relative flex-1">
              <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base text-foreground-400" />
              <input
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder={scope === "news" ? "搜索资讯关键词…" : "搜索作品关键词…"}
                className="w-full rounded-md border border-background-300 bg-background-50 py-2.5 pl-10 pr-4 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
              />
            </div>
            <button
              type="submit"
              className="whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-accent-600"
            >
              搜索
            </button>
          </form>
        </div>
      )}

      {menuOpen && (
        <div className="md:hidden border-t border-background-200 bg-background-50">
          <nav className="mx-auto flex max-w-3xl flex-col px-4 py-3">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                className={({ isActive }) =>
                  `rounded-md px-4 py-3 text-base font-medium transition-colors ${
                    isActive
                      ? "bg-primary-50 text-primary-700"
                      : "text-foreground-800 hover:bg-background-100"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              className="mt-2 inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 px-4 py-3 text-base font-medium text-background-50"
            >
              <i className="ri-mail-send-line" />
              合作咨询
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}